/**
 * What the register can see, against what the statistics office measured.
 *
 * THE TWO BARS ARE NOT A BEFORE AND AN AFTER. The pale bar is how many
 * enterprises in that industry told Statistics Estonia they have a website. The
 * solid bar is how many companies told the BUSINESS REGISTER a web address. The
 * register's field is free text nobody is obliged to fill in, so the distance
 * between the bars is the size of our own blind spot - not a market of
 * companies waiting to be sold a website.
 *
 * That distinction is the entire reason this card exists. `webUrl IS NULL`
 * covers 345 000 rows and is the most tempting audience filter in the whole
 * module; read as "has no website" it is wrong for most of them, and the only
 * way that caveat stays in an operator's head is if the screen keeps drawing it.
 *
 * ⚠ THE PALE BAR IS A FLOOR, NOT A FIGURE. The survey behind it asks
 * enterprises with at least ten employees. Estonia has 159 827 active
 * enterprises and 152 205 of them are smaller than that, so the benchmark
 * speaks for roughly one company in twenty of the ones we hold. Read every pale
 * bar as "at least this many". The size strip at the bottom of the card is
 * there to make that concrete: the survey's own smallest band is already its
 * weakest number.
 *
 * INTERNAL ONLY. Nothing here is enrichment, nothing is written to a company
 * row, and no figure on this card belongs in a letter without the scope
 * sentence that sits above it.
 */
import { BarChart3, Info } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { SectorRow, SectorView } from "../../../../lib/marketingOverview";
import { Note, Panel, Stat, num } from "../shared";
import { sectorChart, sectorCoveredTotal } from "./lib";

/** A percentage with one decimal, in the locale the rest of the module counts
 *  in. `5.9` reads as "5,9%" beside "29 054" rather than as "5.9%". */
function share(n: number | null | undefined): string {
  if (n == null) return "-";
  return `${n.toLocaleString("et-EE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/** The comparison track's height. Wide enough that a 1.7% sliver is still a
 *  visible mark rather than a hairline that reads as nothing. */
const TRACK = 9;

/** What the register knows, drawn on top of what the survey found. Both are
 *  shares of the same 0-100 axis, so the bars can be read against each other
 *  and against every other row without a legend of scales. */
function SectorBar({ row }: { row: SectorRow }) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-full"
      style={{ height: TRACK, background: "var(--crm-sunk)" }}
    >
      <i
        className="absolute inset-y-0 left-0 block rounded-full"
        style={{
          width: `${Math.min(100, row.statPct ?? 0)}%`,
          background: "color-mix(in oklab, var(--color-foreground) 16%, transparent)",
        }}
      />
      <i
        className="absolute inset-y-0 left-0 block rounded-full"
        style={{
          // A share that rounds to nothing still gets a mark. A bar that
          // vanishes reads as "we know of none", which for a sector holding
          // 18 242 companies is a different and much stronger claim.
          width: `${Math.max(row.withWeb > 0 ? 1.2 : 0, Math.min(100, row.mirrorPct))}%`,
          background: "linear-gradient(90deg, var(--color-primary), var(--color-primary-2))",
        }}
      />
    </div>
  );
}

export function SectorCard({ view }: { view: SectorView }) {
  const t = useT();
  const chart = sectorChart(view);
  const covered = sectorCoveredTotal(view);
  const mirrorPct = view.total > 0 ? Math.round((view.withWeb / view.total) * 1000) / 10 : 0;

  return (
    <Panel
      title={t("marketing.overview.sectors.title")}
      description={t("marketing.overview.sectors.intro", { year: view.benchmark.year })}
      icon={<BarChart3 className="size-4" />}
    >
      <Note tone="warn" icon={<Info className="size-3.5" />} className="mb-4">
        {t("marketing.overview.sectors.scope", { min: view.benchmark.minEmployees })}
      </Note>

      <div className="crm-pstats mb-4">
        <Stat
          label={t("marketing.overview.sectors.headline.mirror")}
          value={share(mirrorPct)}
          sub={t("marketing.overview.sectors.headline.mirrorSub", {
            n: num(view.withWeb),
            total: num(view.total),
          })}
          size="lg"
        />
        <Stat
          label={t("marketing.overview.sectors.headline.stat")}
          value={share(view.benchmark.economyPct)}
          sub={t("marketing.overview.sectors.headline.statSub", { min: view.benchmark.minEmployees })}
          tone="muted"
          size="lg"
        />
      </div>

      {chart.rows.length === 0 ? (
        <p className="crm-sub">{t("marketing.overview.sectors.empty")}</p>
      ) : (
        <>
          <div className="crm-mut mb-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
            <span className="flex items-center gap-1.5">
              <i
                className="block size-2.5 rounded-full"
                style={{ background: "linear-gradient(90deg, var(--color-primary), var(--color-primary-2))" }}
              />
              {t("marketing.overview.sectors.legend.mirror")}
            </span>
            <span className="flex items-center gap-1.5">
              <i
                className="block size-2.5 rounded-full"
                style={{ background: "color-mix(in oklab, var(--color-foreground) 16%, transparent)" }}
              />
              {t("marketing.overview.sectors.legend.stat", { year: view.benchmark.year })}
            </span>
          </div>

          <ul className="space-y-2.5">
            {chart.rows.map((row) => (
              <li key={row.section}>
                <div className="flex items-baseline justify-between gap-3 text-[12px]">
                  {/* `min-w-0` on the growing half, `shrink-0` on the numbers:
                      without both, a long industry name pushes the figures off
                      the right edge instead of truncating itself. */}
                  <span className="min-w-0 flex-1 truncate">
                    <b className="crm-tnum mr-1.5 text-muted-foreground">{row.section}</b>
                    {t(`marketing.overview.sectors.name.${row.section}`)}
                  </span>
                  <span
                    className="crm-tnum shrink-0"
                    title={t("marketing.overview.sectors.rowTitle", {
                      n: num(row.withWeb),
                      total: num(row.total),
                    })}
                  >
                    {share(row.mirrorPct)}
                    <span className="crm-mut"> / {share(row.statPct)}</span>
                  </span>
                </div>
                <div className="mt-1">
                  <SectorBar row={row} />
                </div>
              </li>
            ))}
          </ul>

          <div className="crm-sub mt-4">
            {t("marketing.overview.sectors.covered", {
              covered: num(covered),
              total: num(view.total),
              outside: num(chart.outsideSurvey + view.unclassified),
            })}
          </div>
        </>
      )}

      {view.benchmark.sizes.length > 0 ? (
        <div className="mt-5 border-t border-[var(--crm-line)] pt-4">
          <div className="crm-sub mb-2.5">
            {t("marketing.overview.sectors.sizes.intro", { min: view.benchmark.minEmployees })}
          </div>
          <ul className="space-y-2">
            {view.benchmark.sizes
              .filter((s) => s.band !== "TOTAL")
              .map((s) => (
                <li key={s.band} className="flex items-center gap-3 text-[12px]">
                  {/* The band alone. The intro sentence above already says
                      these are employee counts, and repeating the noun on
                      every row wrapped four labels of five onto a second
                      line while the fifth stayed on one - five rows that
                      did not line up, for a word the reader had just read. */}
                  <span className="w-[58px] shrink-0 crm-tnum crm-mut">
                    {t(`marketing.overview.sectors.sizes.band.${s.band}`)}
                  </span>
                  <span
                    className="relative min-w-0 flex-1 overflow-hidden rounded-full"
                    style={{ height: 6, background: "var(--crm-sunk)" }}
                  >
                    <i
                      className="absolute inset-y-0 left-0 block rounded-full"
                      style={{
                        width: `${Math.min(100, s.webPct ?? 0)}%`,
                        background: "color-mix(in oklab, var(--color-foreground) 28%, transparent)",
                      }}
                    />
                  </span>
                  <span className="crm-tnum w-[52px] shrink-0 text-right">{share(s.webPct)}</span>
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      {/* The licence asks for attribution wherever the figures appear, and the
          retrieval date is what tells a reader next autumn that the snapshot is
          a year old. Both come from the generated file rather than from this
          component, so a refresh moves them without a second edit here. */}
      <p className="crm-mut mt-4 text-[11px]">
        {view.benchmark.attribution}
        {" - "}
        {t("marketing.overview.sectors.retrieved", { date: view.benchmark.retrieved })}
      </p>
    </Panel>
  );
}
